/**
 * floatingNavInset.ts — a surface whose nav FLOATS over the top of the page
 * tells the notice band where that nav ends (owner report 2026-09-17).
 *
 * THE DEFECT, as reported on the home screen. `/` renders with no in-flow
 * <Header/> (App.tsx's shouldShowHeader excludes it). Its nav is
 * LandingPage's HomeHeader: a `position: fixed` pill row at the top right,
 * floating over the top of the document — which is precisely where
 * GlobalBannerCenter's band renders in normal flow. The band carries no
 * z-index (deliberately: it is page chrome, and raising it would bury the
 * nav's own notification and mobile-menu dropdowns, which hang DOWN through
 * that same strip), so the pills painted over the band's right end: the
 * rotation dots, the dismiss ✕, and on a narrower window part of the notice
 * itself. Owner's words: "it should be the same width, but not be blocked
 * back behind the buttons", and it "needs to be a banner underneath the
 * button".
 *
 * THE FIX. The surface publishes how much of the top of the viewport its nav
 * occupies; the band offsets itself by exactly that much and lands underneath
 * the pills, still full width. The variable is ABSENT wherever nobody
 * publishes it (this hook withdraws the property on unmount), so the band's
 * `var(…, 0px)` fallback keeps every other page byte-identical.
 *
 * ONE MECHANISM, NOT A HOME-SCREEN SPECIAL CASE — which is why nothing here
 * names the home screen. The other header-less routes (/mission, /team,
 * /insights, /pricing, /privacy, /terms, /cookie, /justiceisurright) each
 * float a nav of their own over the same strip, and AppUpdateBanner is
 * deliberately ungated by bannerSurfacePolicy, so the band can speak there
 * too. Adopting this is a ref plus one hook call per page; it was not done in
 * the change that introduced it because only the home screen was reported and
 * those pages could not be checked by eye here.
 *
 * MEASURED, NEVER ASSUMED. On the home screen both the row's top inset and
 * its height are clamp()ed against the viewport (`clamp(1.35rem, 3vh,
 * 2.25rem)` top, `clamp(36px, 3.2vw, 40px)` per pill) and the row sheds its
 * four nav pills below 768px, so no constant is right at more than one size.
 * For a FIXED element `getBoundingClientRect().bottom` is viewport-relative
 * and does not move with scroll, which is exactly the number wanted.
 *
 * …BUT THE ROW'S OWN BOX IS NOT THE WHOLE ROW, measured on live prest0.ai
 * 2026-09-17. Below 768px the home nav hides its four pills and its remaining
 * control — the menu toggle — is `position: absolute`, so it contributes no
 * height and the header's box collapses to 0: bottom 4px, all inset, while a
 * visible 44px button sits at 8..52 right on top of the band. Reading the box
 * alone published 12px there and left the phone exactly as broken as before.
 *
 * So the measurement is the union of the row's box and every VISIBLE
 * descendant that is part of the row, with two exclusions that carry the
 * whole rule:
 *   - a `position: fixed` descendant is a POP-OUT (the notifications panel,
 *     the mobile menu), not part of the row. It opens on demand and must
 *     never push the band down — one of them is 190px tall. Its subtree is
 *     skipped whole.
 *   - anything not painted (`display: none`, `visibility: hidden`, zero
 *     opacity) is skipped with its subtree, which is what keeps a closed
 *     dropdown from counting on the surfaces where it is not fixed.
 * Measured live, the union is identical to the box at 1440 / 1000 / 820
 * (71 / 52 / 52) and corrects 760 and 390 from 4 to 52 — the toggle's own
 * bottom edge.
 *
 * WHAT THIS DOES NOT DO. It moves the band out from under the nav; it does
 * not raise it over the nav. Scrolling still carries the band up behind a
 * fixed row on its way off screen, which is what a fixed row is for — and
 * the alternative (a layering rung above the nav) would open the nav's own
 * dropdowns behind the band.
 */

import { useEffect, type RefObject } from 'react';
import { FLOATING_NAV_INSET_VAR } from './GlobalBannerCenter';

/**
 * Air between the bottom of the nav row and the top of the band, so the two
 * read as separate pieces of chrome rather than one stacked block. Matches
 * the home row's own 0.5rem top inset.
 */
export const FLOATING_NAV_BAND_GAP_PX = 8;

/** The published value for a measured row bottom — the one rounding rule. */
export function floatingNavInsetPx(navBottomPx: number): number {
  if (!Number.isFinite(navBottomPx) || navBottomPx <= 0) return 0;
  return Math.round(navBottomPx) + FLOATING_NAV_BAND_GAP_PX;
}

/** One descendant of the nav row, as the union rule needs to see it. */
export interface NavPartReading {
  /** Its viewport-relative bottom edge. */
  bottom: number;
  /** Zero-height elements occupy nothing, however they are positioned. */
  height: number;
  /** `position: fixed` — a pop-out panel, never part of the row. */
  popOut: boolean;
  /** Actually painted: not display:none, not visibility:hidden, not transparent. */
  painted: boolean;
}

/**
 * How far down the viewport the nav row actually reaches: its own box, raised
 * by any part that is painted, has height, and is not a pop-out.
 *
 * Pure, so the rule is exercised directly rather than through a layout engine
 * — jsdom has no layout, and the shape this corrects (an out-of-flow control
 * outside a collapsed box) is invisible to a renderer that reports every rect
 * as zero.
 */
export function unionNavBottom(rowBottom: number, parts: readonly NavPartReading[]): number {
  let bottom = Number.isFinite(rowBottom) ? rowBottom : 0;
  for (const part of parts) {
    if (part.popOut || !part.painted) continue;
    if (!(part.height > 0) || !Number.isFinite(part.bottom)) continue;
    if (part.bottom > bottom) bottom = part.bottom;
  }
  return bottom;
}

/** Is this element painted at all? Three unambiguous signals, nothing looser. */
function isPainted(style: CSSStyleDeclaration): boolean {
  if (style.display === 'none') return false;
  if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  return parseFloat(style.opacity || '1') !== 0;
}

/**
 * Walk the row and apply {@link unionNavBottom}. A pop-out or unpainted
 * element takes its whole subtree with it — that is what bounds the walk, and
 * what keeps a 190px closed menu from deciding where the band goes.
 */
export function measureFloatingNavBottom(row: HTMLElement): number {
  const parts: NavPartReading[] = [];
  const visit = (el: Element) => {
    for (const child of Array.from(el.children)) {
      let style: CSSStyleDeclaration;
      try {
        style = window.getComputedStyle(child);
      } catch {
        continue; // a browser that cannot answer tells us nothing to add
      }
      if (style.position === 'fixed' || !isPainted(style)) continue;
      const rect = child.getBoundingClientRect();
      parts.push({ bottom: rect.bottom, height: rect.height, popOut: false, painted: true });
      visit(child);
    }
  };
  visit(row);
  return unionNavBottom(row.getBoundingClientRect().bottom, parts);
}

/**
 * Publish this surface's floating-nav inset on <html> while `ref` is mounted;
 * withdraw it on unmount. Safe to call unconditionally — a null ref, an
 * unlaid-out row or a browserless environment simply publishes nothing.
 */
export function useFloatingNavInsetPublisher(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!root) return undefined;

    const publish = () => {
      const el = ref.current;
      if (!el) {
        root.style.removeProperty(FLOATING_NAV_INSET_VAR);
        return;
      }
      const inset = floatingNavInsetPx(measureFloatingNavBottom(el));
      if (inset <= 0) {
        root.style.removeProperty(FLOATING_NAV_INSET_VAR);
        return;
      }
      root.style.setProperty(FLOATING_NAV_INSET_VAR, `${inset}px`);
    };

    publish();

    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(publish);
      if (ref.current) ro.observe(ref.current);
    } catch {
      /* no ResizeObserver — the resize listener below still covers reflows */
    }
    window.addEventListener('resize', publish, { passive: true });

    return () => {
      window.removeEventListener('resize', publish);
      ro?.disconnect();
      // WITHDRAWN ON UNMOUNT, always: leaving it behind would push the band
      // down on every other route for the rest of the session.
      root.style.removeProperty(FLOATING_NAV_INSET_VAR);
    };
  }, [ref]);
}
